'use client';

import { Building2, ChevronRight, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type AccountCategory = 'individual' | 'organization';

interface AccountCategoryStepProps {
  onSelect: (category: AccountCategory) => void;
}

interface CategoryOption {
  category: AccountCategory;
  icon: React.ReactNode;
  labelKey: string;
  examplesKey: string;
}

const categoryOptions: CategoryOption[] = [
  {
    category: 'organization',
    icon: <Building2 className="h-8 w-8" strokeWidth={1.2} />,
    labelKey: 'profileWizard.categoryOrganization',
    examplesKey: 'profileWizard.categoryOrganizationExamples',
  },
  {
    category: 'individual',
    icon: <User className="h-8 w-8" strokeWidth={1.2} />,
    labelKey: 'profileWizard.categoryIndividual',
    examplesKey: 'profileWizard.categoryIndividualExamples',
  },
];

export function AccountCategoryStep({ onSelect }: AccountCategoryStepProps) {
  const { t } = useTranslation('account');

  return (
    <div className="space-y-4 py-2">
      <div className="text-center">
        <h3 className="text-lg font-medium">
          {t('profileWizard.categoryStepTitle')}
        </h3>
        <p className="text-muted-foreground mt-1 text-sm">
          {t('profileWizard.categoryStepSubtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {categoryOptions.map((option) => (
          <button
            key={option.category}
            type="button"
            onClick={() => onSelect(option.category)}
            className="group hover:border-primary hover:bg-muted/50 focus-visible:ring-ring flex w-full items-center gap-4 rounded-xl border-2 px-5 py-5 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            <div className="text-muted-foreground shrink-0">{option.icon}</div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{t(option.labelKey)}</div>
              <div className="text-muted-foreground mt-0.5 text-xs">
                {t(option.examplesKey)}
              </div>
            </div>
            <ChevronRight className="text-muted-foreground group-hover:text-foreground h-5 w-5 shrink-0 transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
}
